<template>
  <v-card
    class="settings-card"
    elevation="3"
    rounded="lg"
  >
    <v-form 
      @submit.prevent
      ref="settings_form"
      :disabled="plan_locked"
    >
      <v-card-title class="text-h5 font-weight-medium">
        <v-icon left color="primary">mdi-package-variant-closed</v-icon>
        Fulfillment & Products
        <v-spacer></v-spacer>
        <SettingsSaveStatus :state="save_state" />
      </v-card-title>

      <v-card-text class="pb-0">
        <v-alert
          type="info"
          class="mb-4"
          v-if="plan_locked"
          elevation="2"
          border="left"
          dense
        >
          These settings can't be edited on your current plan.
        </v-alert>

        <!-- Picking -->
        <section class="settings-section">
          <div class="section-header">
            <v-icon color="primary" small class="mr-2">mdi-cart-outline</v-icon>
            <span class="section-title">Picking</span>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <div class="field-label">
                Parcels per round
                <ToolTip>
                  The amount of orders picked simultaneously, ie how many boxes you have on your cart
                </ToolTip>
              </div>
              <v-slider
                v-model="order_bucket_size"
                thumb-label="always"
                class="settings-slider"
                :max="20"
                :min="1"
                @change="save('order_bucket_size')"
                :rules="[
                  v => v <= get_plan.max_order_bucket_size || `Max ${get_plan.max_order_bucket_size} parcels per round. Upgrade plan to get more.`
                ]"
              ></v-slider>
            </v-col>
            <v-col cols="12" md="6">
              <v-switch
                v-model="group_orders_by_similarity"
                hide-details
                class="mt-1"
                @change="save('group_orders_by_similarity')"
              >
                <template v-slot:label>
                  Group orders by similarity
                  <ToolTip>
                    Automatically select the {{ order_bucket_size }} most similar orders to make picking most efficient. If this is not selected, orders will be picked oldest to newest.
                  </ToolTip>
                </template>
              </v-switch>
            </v-col>
          </v-row>
        </section>

        <v-divider></v-divider>

        <!-- Scan verification -->
        <section class="settings-section">
          <div class="section-header">
            <v-icon color="primary" small class="mr-2">mdi-barcode-scan</v-icon>
            <span class="section-title">Scan verification</span>
            <ToolTip>
              Require scanning during picking and packing to make sure the right products end up in the right parcels.
            </ToolTip>
          </div>
          <v-row>
            <v-col cols="12" md="4">
              <v-switch
                v-model="require_shelf_scanning"
                hide-details
                class="mt-1"
                @change="save('require_shelf_scanning')"
                :disabled="!get_plan || !get_plan.allow_shelf_scanning"
              >
                <template v-slot:label>
                  Scan shelf codes when picking
                  <ToolTip>
                    Print QR codes or barcodes for all shelf positions and require these to be scanned when items are being picked to ensure the right shelf gets picked from.
                  </ToolTip>
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" md="4">
              <v-switch
                v-model="require_pick_scanning"
                hide-details
                class="mt-1"
                @change="save('require_pick_scanning')"
                :disabled="!get_plan || !get_plan.allow_product_scanning"
              >
                <template v-slot:label>
                  Scan product barcodes when picking
                  <ToolTip>
                    When an item has a barcode, require it to be scanned when picking from the shelf to ensure the correct product gets picked.
                  </ToolTip>
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" md="4">
              <v-switch
                v-model="require_item_scanning"
                hide-details
                class="mt-1"
                @change="save('require_item_scanning')"
                :disabled="!get_plan || !get_plan.allow_product_scanning"
              >
                <template v-slot:label>
                  Scan product barcodes when packing
                  <ToolTip>
                    When an item has a barcode, require it to be scanned when packing parcels to ensure the correct product is being shipped.
                  </ToolTip>
                </template>
              </v-switch>
            </v-col>
          </v-row>

          <v-expand-transition>
            <div v-if="any_scanning" class="sub-options">
              <div v-if="require_shelf_scanning" class="sub-option">
                <div class="field-label">
                  Require shelf scan for
                  <ToolTip>
                    "Items without barcode" skips the shelf scan when the product itself has a barcode, useful when product barcode scanning already verifies the pick.
                  </ToolTip>
                </div>
                <v-btn-toggle
                  :value="shelf_scanning_mode || 'all'"
                  mandatory
                  rounded
                  @change="set_company_field('shelf_scanning_mode', $event)"
                >
                  <v-btn value="all" small>All items</v-btn>
                  <v-btn value="no_barcode" small>Items without barcode</v-btn>
                </v-btn-toggle>
              </div>
              <div class="sub-option">
                <div class="field-label">
                  Scanning method
                  <ToolTip>
                    Applies to shelf codes and product barcodes. Use camera scans on phones/tablets, or scanner mode for handheld devices like Zebra scanners that type the scanned value into the app. In scanner mode, print your shelf labels with a barcode (not only a QR code) unless your scanner can read QR codes.
                  </ToolTip>
                </div>
                <v-btn-toggle
                  :value="uses_barcode_scanner ? 'scanner' : 'camera'"
                  mandatory
                  rounded
                  @change="set_barcode_scanning_method"
                >
                  <v-btn value="camera" small>
                    <v-icon left small>mdi-camera</v-icon>
                    Use camera
                  </v-btn>
                  <v-btn value="scanner" small>
                    <v-icon left small>mdi-barcode-scan</v-icon>
                    Use scanner
                  </v-btn>
                </v-btn-toggle>
              </div>
            </div>
          </v-expand-transition>
        </section>

        <v-divider></v-divider>

        <!-- Inventory counts -->
        <section class="settings-section">
          <div class="section-header">
            <v-icon color="primary" small class="mr-2">mdi-clipboard-check-outline</v-icon>
            <span class="section-title">Inventory counts</span>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <div class="field-label">
                Request count when item inventory drops below
                <ToolTip>
                  When an article drop below this amount according to Shopify, we will request the picker to double check by counting the shelf. If it's inaccurate, the inventory in Shopify will be updated with what's actually on the shelf and an alert will be created for the admin.
                </ToolTip>
              </div>
              <v-slider
                v-model="check_inventory_threshold"
                thumb-label="always"
                class="settings-slider"
                :max="20"
                :min="0"
                @change="save('check_inventory_threshold')"
              ></v-slider>
            </v-col>
            <v-col cols="12" md="6">
              <div class="field-label">
                Redo inventory count after (days)
                <ToolTip>
                  If an article has been counted once, this is for how long we will consider that count accurate before we are asking for another re-count (in the event the article quantity is below the threshold)
                </ToolTip>
              </div>
              <v-slider
                v-model="days_valid_inventory_count"
                thumb-label="always"
                class="settings-slider"
                :max="365"
                :min="1"
                @change="save('days_valid_inventory_count')"
              ></v-slider>
            </v-col>
          </v-row>
        </section>

        <v-divider></v-divider>

        <!-- Products -->
        <section class="settings-section">
          <div class="section-header">
            <v-icon color="primary" small class="mr-2">mdi-sync</v-icon>
            <span class="section-title">Products</span>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <div class="field-label">
                Auto sync products every (hours)
                <ToolTip>
                  Syncing often (if you have a lot of products) slows down opening {{ $appName }}. Note: Everytime a product gets picked, that product will automatically sync!
                </ToolTip>
                <v-btn 
                  x-small
                  outlined
                  class="ml-2"
                  @click="sync_products"
                  :loading="loading_products"
                >
                  Sync now
                </v-btn>
              </div>
              <v-slider
                v-model="products_refresh_interval"
                thumb-label="always"
                class="settings-slider"
                :max="100"
                :min="1"
                @change="save('products_refresh_interval')"
              ></v-slider>
            </v-col>
          </v-row>
        </section>

        <v-divider></v-divider>

        <!-- Packing & shipping -->
        <section class="settings-section">
          <div class="section-header">
            <v-icon color="primary" small class="mr-2">mdi-truck-outline</v-icon>
            <span class="section-title">Packing & shipping</span>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-switch
                v-model="require_fulfillment_photos"
                hide-details
                class="mt-1"
                @change="save('require_fulfillment_photos')"
              >
                <template v-slot:label>
                  Require fulfillment photos
                  <ToolTip>
                    Ask staff to take a photo of items before fulfilling, and save the photo on the parcel for future reference and cross checks.
                  </ToolTip>
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12">
              <div class="field-label">
                Prepare shipping labels
                <ToolTip>
                  <div class="mb-2">Controls when the shipping label for a parcel is booked with the carrier.</div>
                  <div class="mb-2"><strong>When all items in a parcel are picked (default):</strong> the label is booked in the background as soon as the last item for that parcel is picked, so it is already printed when you reach the packing step. This is the fastest way to pack.</div>
                  <div class="mb-2"><strong>When the parcel is due to be packed:</strong> the label is booked when the parcel becomes active at the packing step. You wait for the carrier on every parcel, but no label is booked for a parcel that gets skipped.</div>
                  <div><strong>Manually:</strong> nothing is booked automatically. Press "Generate label" on each parcel when you are ready.</div>
                </ToolTip>
              </div>
              <v-btn-toggle
                :value="label_generation_mode || 'on_picked'"
                mandatory
                rounded
                class="label-mode-toggle"
                @change="set_company_field('label_generation_mode', $event)"
              >
                <v-btn value="on_picked" small>When all items in a parcel are picked</v-btn>
                <v-btn value="on_pack" small>When the parcel is due to be packed</v-btn>
                <v-btn value="manual" small>Manually</v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
        </section>
      </v-card-text>

    </v-form>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import ToolTip from '@/components/ToolTip.vue'
import SettingsSaveStatus from '@/components/SettingsSaveStatus.vue'
import company_settings from '@/mixins/company_settings'

// Settings > Fulfillment & Products. Needs the `settings.company` scope. Returns settings live in SettingsReturnsCard.
export default {
  components: {
    ToolTip,
    SettingsSaveStatus,
  },
  mixins: [company_settings],
  computed: {
    ...mapState({
      loading_products: state => state.product.loading_products,
    }),
    ...mapFields([
      'warehouse.company.products_refresh_interval',
      'warehouse.company.order_bucket_size',
      'warehouse.company.days_valid_inventory_count',
      'warehouse.company.check_inventory_threshold',
      'warehouse.company.require_item_scanning',
      'warehouse.company.require_pick_scanning',
      'warehouse.company.uses_barcode_scanner',
      'warehouse.company.require_shelf_scanning',
      'warehouse.company.shelf_scanning_mode',
      'warehouse.company.require_fulfillment_photos',
      'warehouse.company.group_orders_by_similarity',
      'warehouse.company.label_generation_mode',
    ]),
    plan_locked(){
      return !!this.get_plan && this.get_plan.id === 1
    },
    any_scanning(){
      return this.require_shelf_scanning || this.require_pick_scanning || this.require_item_scanning
    },
  },
  methods: {
    set_barcode_scanning_method(method){
      if(!method) return
      this.uses_barcode_scanner = method === 'scanner'
      this.save('uses_barcode_scanner')
    },
    async sync_products(){
      await this.$store.dispatch('product/get_products', { company_uid: this.company.id, refresh: true})
    }
  },
}
</script>

<style lang="scss" scoped>
@import '~@/sass/settings-card';
</style>
